'use client'

import { useAccountStore } from '@/store'
import { useQuery } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect, useState } from 'react'
import { fetchAddressType, setShippingAddressV2 } from '../services/address'
import { DEFAULT_SHIPPING_METHOD } from './use-shipping-method'

interface UseAddressManagementProps {
  cartId: string
  refetchCart: () => void
  dataCities: City[] | undefined
  dataDistricts: District[] | undefined
  dataWards: Ward[] | undefined
  mutateShippingMethod: ({ carrier_code, method_code }: TShippingMethod) => void
}

export const useAddressManagement = ({
  cartId,
  refetchCart,
  dataCities,
  dataDistricts,
  dataWards,
  mutateShippingMethod,
}: UseAddressManagementProps) => {
  const { data: userInfo } = useAccountStore()
  const [selectAddress, setSelectAddress] = useState<IAddressCustomer>({} as IAddressCustomer)

  const onSubmitShippingAddress = useCallback(
    async (data: any) => {
      let dataAddress: TDataAddress

      if (!isEmpty(userInfo)) {
        dataAddress = {
          customer_address_id: selectAddress?.id || data,
        }
      } else {
        const district =
            dataDistricts?.find((district) => district.code == data?.district_code)?.default_name || '.',
          ward = dataWards?.find((ward) => ward.code == data?.ward_code)?.default_name || '.',
          city = dataCities?.find((city) => city.code == data?.province_code)?.default_name || '.'
        dataAddress = {
          address: {
            country_code: 'VN',
            company: '',
            lastname: data?.name?.trim(),
            firstname: '.',
            postcode: data?.postcode || '',
            street: ['' + (data?.street || '').trim()],
            telephone: data?.phone,
            save_in_address_book: false,
            ward_code: data?.ward_code || '',
            ward_name: ward,
            city: city,
            address_type: data?.addressType || '',
            province_code: data?.province_code || '',
            province_name: city,
            district_code: data?.district_code || '',
            district_name: district,
          },
        }
      }
      const dataSubmit: IShippingAddressPayloadV2 = {
        inputSetShippingAddresses: {
          cart_id: cartId,
          shipping_addresses: [dataAddress],
        },
      }
      try {
        await setShippingAddressV2(dataSubmit)
        await refetchCart()
        mutateShippingMethod(DEFAULT_SHIPPING_METHOD)
      } catch (error) {
        console.error('Error setting shipping address:', error)
      }
    },
    [cartId, dataCities, dataDistricts, dataWards, selectAddress?.id, userInfo]
  )

  const handleSelectAddress = useCallback(
    (e: any, data: IAddressCustomer) => {
      if ((!!e && e?.target?.closest('.btn-del')) || e?.target?.closest('.btn-change')) return
      setSelectAddress(data)
      onSubmitShippingAddress(data?.id)
    },
    [onSubmitShippingAddress]
  )

  const listAddress = useCallback(() => {
    return userInfo?.addresses || []
  }, [userInfo?.addresses])

  useEffect(() => {
    if (!!userInfo?.addresses?.length) {
      if (userInfo?.default_shipping) {
        handleSelectAddress(
          null,
          listAddress().find((el) => el.id == userInfo?.default_shipping) || listAddress()[0]
        )
      } else {
        handleSelectAddress(null, listAddress()?.[0])
      }
    }
  }, [handleSelectAddress, listAddress, userInfo?.addresses?.length, userInfo?.default_shipping])

  return {
    selectAddress,
    handleSelectAddress,
    onSubmitShippingAddress,
    listAddress,
  }
}
// const { data: listAddressTypes } = useAddressTypes()
// const currentAddressType = watch('addressType', '')
// const isExistDistrictField = useMemo(() => {
//   const availableFields = listAddressTypes?.find((type) => type.type_code === currentAddressType)?.fields
//   return !!availableFields?.find((field) => field.field_code === AddressCode.DISTRICT)
// }, [currentAddressType, listAddressTypes])
// const listFieldAddress = useMemo(() => {
//   if (!listAddressTypes) return []
//   return listAddressTypes.find((type) => type.type_code === currentAddressType)?.fields || []
// }, [listAddressTypes, currentAddressType])

// const { dataCities } = useCities(currentAddressType)
// const { dataDistricts } = useDistrict(currentAddressType, watch('city_id'), isExistDistrictField)
// const { dataWards } = useWards(currentAddressType, watch('district_code'))
export const useAddressTypes = () => {
  return useQuery({
    queryKey: ['address-types'],
    queryFn: () => fetchAddressType(),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}
